import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Camera, ScanLine, X, Image as ImageIcon } from 'lucide-react';
import PrimaryButton from '../components/PrimaryButton';
import { scanApi } from '../services/gardenApi';
import { convertHeicToJpeg, isHeicFile } from '../../utils/convertHeic';
import { useGardenPageTitle } from '../useGardenPageTitle';

// Step 1 of the Scan Plant Tag flow. The user either snaps a new photo (camera)
// or picks an existing image. We then POST it to /api/garden/scan/tag and
// navigate to the confirmation screen. Never blocks the user: if scanning
// fails they can still continue and fill the form manually.
export default function ScanPlantTagScreen() {
  useGardenPageTitle('Scan plant tag', 'Scan plant tag');
  const navigate = useNavigate();
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!preview) return undefined;
    return () => URL.revokeObjectURL(preview);
  }, [preview]);

  const onPicked = async (e) => {
    let f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    setError(null);

    if (isHeicFile(f)) {
      try {
        f = await convertHeicToJpeg(f);
      } catch (err) {
        setError(err.message || 'Could not convert HEIC image. Please try a JPG or PNG.');
        return;
      }
    }

    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const clearPick = () => {
    setFile(null);
    setPreview(null);
    setError(null);
  };

  const runScan = async () => {
    if (!file || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await scanApi.tag(file);
      // Hand the extracted data (and the persisted image URL) to the
      // confirmation screen via location.state. Keep it simple — a single
      // page reload will reset state, which is fine for MVP.
      navigate('/garden/plants/scan/confirm', {
        state: {
          scan: {
            image_url: r.image_url || null,
            ocr_text: r.ocr_text || '',
            plant: r.plant || {},
            confidence: r.confidence || null,
            ai_available: r.ai_available !== false,
            ai_failed: !!r.ai_failed,
          },
        },
      });
    } catch (e) {
      // Real network / auth errors are rare (AI failures come back as 200
      // with empty fields). Let the user retry or continue manually.
      setError(e.message || 'We couldn\u2019t scan that photo. Try again or continue without scanning.');
      setBusy(false);
    }
  };

  const continueManual = () => {
    navigate('/garden/plants/new');
  };

  return (
    <div className="pb-32">
      <header className="flex items-center gap-2 px-2 pt-3 pb-2">
        <button
          onClick={() => navigate(-1)}
          className="min-h-11 min-w-11 rounded-full flex items-center justify-center text-garden-muted"
          aria-label="Back"
        >
          <ArrowLeft size={22} />
        </button>
        <h1 className="flex-1 text-lg font-semibold text-garden-ink truncate">
          Scan plant tag
        </h1>
      </header>

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*,image/heic,image/heif,.heic,.heif"
        capture="environment"
        className="hidden"
        onChange={onPicked}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,image/heic,image/heif,.heic,.heif"
        className="hidden"
        onChange={onPicked}
      />

      <div className="px-4 pt-2">
        <div className="rounded-2xl bg-white border border-black/5 shadow-sm p-4">
          <div className="aspect-[3/4] rounded-2xl bg-garden-primaryLight overflow-hidden flex items-center justify-center relative">
            {preview ? (
              <>
                <img src={preview} alt="" className="w-full h-full object-contain" />
                <button
                  type="button"
                  onClick={clearPick}
                  disabled={busy}
                  className="absolute top-2 right-2 w-9 h-9 rounded-full bg-black/60 text-white flex items-center justify-center disabled:opacity-50"
                  aria-label="Remove photo"
                >
                  <X size={18} />
                </button>
              </>
            ) : (
              <div className="flex flex-col items-center text-garden-primaryDark px-6 text-center">
                <ScanLine size={44} />
                <div className="mt-3 text-sm font-medium">
                  Take a clear photo of the plant tag
                </div>
                <div className="mt-1 text-xs text-garden-muted">
                  Good light, flat tag, text in focus
                </div>
              </div>
            )}
          </div>

          {!preview ? (
            <div className="mt-4 space-y-2">
              <PrimaryButton
                onClick={() => cameraInputRef.current?.click()}
                className="w-full"
              >
                <Camera size={18} /> Take photo
              </PrimaryButton>
              <PrimaryButton
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
                className="w-full"
              >
                <ImageIcon size={18} /> Choose from library
              </PrimaryButton>
            </div>
          ) : null}
        </div>

        {error ? (
          <div className="mt-3 p-3 rounded-xl bg-red-50 text-sm text-red-700">
            {error}
          </div>
        ) : null}
      </div>

      {preview ? (
        <div className="fixed bottom-16 left-0 right-0 z-20 bg-white/95 backdrop-blur border-t border-black/5 p-3">
          <div className="mx-auto max-w-md space-y-2">
            <PrimaryButton
              onClick={runScan}
              disabled={busy}
              className="w-full"
            >
              {busy ? 'Reading tag\u2026' : 'Scan & continue'}
            </PrimaryButton>
            <button
              type="button"
              onClick={continueManual}
              disabled={busy}
              className="w-full min-h-10 text-sm text-garden-muted underline underline-offset-2 disabled:opacity-50"
            >
              Skip and enter manually
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
